import { useRef, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { usePopupTransition } from '@/shared/hooks/usePopupTransition';
import { useModalLock } from '@/shared/hooks/useModalLock';
import { useCloseOnBack } from '@/shared/hooks/useCloseOnBack';
import { IconClose } from '@/shared/ui/icons';
import styles from './BottomSheet.module.less';

interface BottomSheetProps {
  isOpen: boolean;
  onClose: () => void;
  children: ReactNode;
  /** Заголовок панели. Вместе с ним появляется крестик */
  title?: string;
  /** Строка под заголовком: количество, сумма */
  subtitle?: ReactNode;
  /** Слева от крестика, на строке заголовка */
  headAside?: ReactNode;
  /**
   * panel — белая коробка с заголовком, вид по умолчанию.
   * actions — системный набор действий в стиле iOS: содержимое в отдельной
   * карточке, отмена — второй карточкой под ней. Подпись отмены включает
   * этот вид сама, явно он нужен, когда кнопку рисует сама шторка
   */
  variant?: 'panel' | 'actions';
  /** Подпись кнопки отмены в наборе действий */
  cancelLabel?: string;
  /** Содержимое прижимается к краям панели — для списков со своей прокруткой */
  flush?: boolean;
}

/**
 * Нижняя шторка. Без cancelLabel — панель со скруглённым верхом и крестиком,
 * с ним — системный набор действий, где отмена стоит отдельной кнопкой.
 */
export function BottomSheet({
  isOpen,
  onClose,
  children,
  title,
  subtitle,
  headAside,
  variant = 'panel',
  cancelLabel,
  flush = false,
}: BottomSheetProps) {
  const { visible, closing } = usePopupTransition(isOpen, 250);
  const overlayRef = useRef<HTMLDivElement>(null);
  const sheetRef = useRef<HTMLDivElement>(null);

  useModalLock(visible, overlayRef, sheetRef);
  useCloseOnBack(isOpen, onClose);

  if (!visible) return null;

  const isActions = variant === 'actions' || Boolean(cancelLabel);

  const body = isActions ? (
    children
  ) : (
    <div className={styles.panel}>
      {title && (
        <div className={styles.head}>
          <div className={styles.headText}>
            <div className={styles.title}>{title}</div>
            {subtitle && <div className={styles.subtitle}>{subtitle}</div>}
          </div>

          <div className={styles.headActions}>
            {headAside}
            <button
              type="button"
              className={styles.closeBtn}
              onClick={onClose}
              aria-label="Закрыть"
            >
              <IconClose className={styles.closeIcon} />
            </button>
          </div>
        </div>
      )}

      <div
        className={[
          styles.body,
          title ? styles.bodyWithHead : '',
          flush ? styles.bodyFlush : '',
        ]
          .filter(Boolean)
          .join(' ')}
      >
        {children}
      </div>
    </div>
  );

  return createPortal(
    <div
      ref={overlayRef}
      className={`${styles.overlay} ${closing ? styles.overlayClosing : ''}`}
      onClick={onClose}
    >
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        tabIndex={-1}
        className={[
          styles.sheet,
          isActions ? styles.sheetActions : '',
          closing ? styles.sheetClosing : '',
        ]
          .filter(Boolean)
          .join(' ')}
        onClick={(e) => e.stopPropagation()}
      >
        {body}

        {cancelLabel && (
          <button
            type="button"
            className={`${styles.group} ${styles.cancel}`}
            onClick={onClose}
          >
            {cancelLabel}
          </button>
        )}
      </div>
    </div>,
    document.body
  );
}

export { styles as bottomSheetStyles };
